import React from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, Modal, ModalBody } from '@razorpay/blade/components';
import { startOfMonth } from 'date-fns';
import { useFormWithProvider } from 'forms';
import { Wizard, WizardStep } from 'forms/Wizard';
import api from 'api';
import {
  WizardError,
  WizardHeader,
  WizardLoader,
  WizardWrapper,
} from 'components/WizardViews/components';
import { WizardInfoProvider } from 'components/WizardViews/providers';
import { useSearchParams } from 'hooks/useSearchParams';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import WizardPathToStepController from '../components/WizardPathToStepController';
import ReviewStep from './ReviewStep';
import UpdateStructureStep from './UpdateStructureStep';
import SalaryDetailsStep from './SalaryDetailsStep';
import {
  revisePayRouteToStepMap,
  revisePayRouteToStepMapWithMSS,
  revisePaySteps,
  ReviseSalaryFormData,
} from './constants';
import { Sidebar } from './components/Sidebar';
import { salaryStructureQueries } from 'components/SalaryStructures/constants';
import { useListSalaryStructures } from 'components/SalaryStructures/hooks';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';
import styled from 'styled-components';

const StyledRevisePayStepWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  height: 100%;
  scroll-behavior: smooth;
`;

const RevisePayWizard = () => {
  const history = useHistory();

  const { setFormData, formData, setFieldValue } = useFormWithProvider<ReviseSalaryFormData>();

  const { isM3S1Enabled } = useIsFeatureDisabledForRearch();
  const loggedInUserId = useSelector(loggedInUserSelectors.peopleId) as number;

  const userId = useSearchParams().urlParams.get('userId') ?? loggedInUserId.toString();

  const payrollSetupQuery = useQuery({
    queryFn: () => api.userQueries.payrollGetSetup(),
    onSuccess: () => ctcQuery.refetch(),
  });

  const ctcQuery = useQuery({
    queryKey: ['getSalaryDetails', userId],
    queryFn: () =>
      api.userQueries.getSalaryDetails({
        userId,
      }),
    enabled: false,
    onSuccess: (data) => {
      if (!data.payrollGetSalaryDetails) return;
      const payrollDetails = data.payrollGetSalaryDetails.data;
      const payrollSetupInfo = payrollSetupQuery.data!.payrollGetSetup.data;

      setFormData({
        ...formData,
        currentAnnualCtc: payrollDetails.annualSalary,
        revisedCompensation: String(payrollDetails.annualSalary),
        currentCtcEffectiveFrom: payrollDetails.effectiveFromDate,
        payrollSetupInfo,
        effectiveFrom: startOfMonth(new Date()),
      });
    },
  });

  const {
    data: salaryStructureData,
    isFetching: isFetchingSalaryStructures,
    error: listSalaryStructureError,
  } = useListSalaryStructures({
    enabled: isM3S1Enabled,
    queryKey: [salaryStructureQueries.getSalaryStructuresList],
    filters: 'SALARY_STRUCTURE_STATUS_ACTIVE',
    onSuccess: (data) => {
      if (data && !formData.salaryStructureData) {
        setFieldValue('salaryStructureData', data);
      }
    },
  });

  const handleCloseClick = () => {
    history.push(`/pay-details/compensation?userId=${userId}`);
  };

  const routeStepMap = isM3S1Enabled ? revisePayRouteToStepMapWithMSS : revisePayRouteToStepMap;

  if (
    ctcQuery.isFetching ||
    payrollSetupQuery.isFetching ||
    !ctcQuery.data ||
    isFetchingSalaryStructures ||
    !formData.effectiveFrom
  ) {
    return <WizardLoader />;
  }

  const error = ctcQuery.error || payrollSetupQuery.error || listSalaryStructureError;

  const payrollDetails = ctcQuery.data.payrollGetSalaryDetails.data;
  const name = payrollDetails.name;
  const firstName = name?.split(' ')[0];

  return (
    <Modal isOpen={true} onDismiss={() => {}} size="full">
      <ModalBody padding="spacing.0">
        <WizardInfoProvider initialStep={0} totalSteps={isM3S1Enabled ? 3 : 2}>
          <WizardPathToStepController routeStepMap={routeStepMap} />
          <WizardWrapper>
            <Box
              borderBottomColor="surface.border.gray.muted"
              borderBottomWidth="thin"
              borderBottomStyle="solid">
              <WizardHeader
                heading="Revise compensation for"
                subHeading={firstName ?? 'Employee'}
                onCloseClick={handleCloseClick}
                isProgressHidden={true}
              />
            </Box>
            <Box display="flex" flex={1} height="100%" overflow="hidden">
              <Box
                padding="spacing.7"
                maxWidth="224px"
                borderRightColor="surface.border.gray.muted"
                backgroundColor="surface.background.gray.moderate"
                borderRightWidth="thin"
                overflowY="auto"
                flexShrink={0}>
                <Sidebar />
              </Box>
              <StyledRevisePayStepWrapper>
                <WizardError onRetry={payrollSetupQuery.refetch} error={error}>
                  <Wizard>
                    {[
                      <WizardStep
                        key="step1"
                        step={revisePaySteps.step1}
                        component={SalaryDetailsStep}
                      />,
                      ...(isM3S1Enabled
                        ? [
                            <WizardStep
                              key="step2"
                              step={revisePaySteps.step2}
                              component={() => (
                                <UpdateStructureStep salaryStructureData={salaryStructureData} />
                              )}
                            />,
                          ]
                        : []),
                      <WizardStep
                        key="step3"
                        step={revisePaySteps.step3}
                        component={() => (
                          <ReviewStep
                            salaryStructure={salaryStructureData?.find(
                              (salaryStructure) =>
                                salaryStructure.id === formData?.salaryStructureId?.[0],
                            )}
                          />
                        )}
                      />,
                    ]}
                  </Wizard>
                </WizardError>
              </StyledRevisePayStepWrapper>
            </Box>
          </WizardWrapper>
        </WizardInfoProvider>
      </ModalBody>
    </Modal>
  );
};

export default RevisePayWizard;
